import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@design-system/components/button'
import { Input } from '@design-system/components/input'
import { Switch } from '@design-system/components/switch'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { organizationsApi } from '@/lib/api'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'
import { formatUsdFromCents } from './formatMoney'

interface OverageControlsProps {
  orgId: string
  allowOverage: boolean
  defaultOverageLimitCents: number | null
  onChange?: () => void
  /** Members see the setting but cannot change it. */
  readOnly?: boolean
  /** Per-credit overage rate in cents (what the cap is enforced at); shown so the bill is explicit. */
  overageRateCents?: number
}

const MIN_CAP_DOLLARS = 100
const DEFAULT_CAP_DOLLARS = 500

/** null = unlimited, number = capped at that many dollars */
type CapMode = 'unlimited' | 'capped'

export function OverageControls(props: OverageControlsProps) {
  const { orgId, allowOverage, defaultOverageLimitCents, onChange, readOnly = false, overageRateCents = 0 } = props
  const { t, i18n } = useTranslation('app')
  const capFormatter = new Intl.NumberFormat(intlLocaleForUi(i18n.language))
  const [pendingToggle, setPendingToggle] = useState(false)
  const [savingCap, setSavingCap] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)

  // Whether the committed setting is unlimited (null) or capped
  const isUnlimited = defaultOverageLimitCents === null

  const committedDollars =
    defaultOverageLimitCents != null
      ? Math.max(MIN_CAP_DOLLARS, Math.round(defaultOverageLimitCents / 100))
      : DEFAULT_CAP_DOLLARS

  // Popover draft state
  const [draftMode, setDraftMode] = useState<CapMode>('unlimited')
  const [draftText, setDraftText] = useState('')

  useEffect(() => {
    if (popoverOpen) {
      setDraftMode(isUnlimited ? 'unlimited' : 'capped')
      setDraftText(String(committedDollars))
    }
  }, [popoverOpen, isUnlimited, committedDollars])

  async function handleToggle(next: boolean) {
    setPendingToggle(true)
    try {
      await organizationsApi.updateOverageSettings(orgId, {
        allowOverage: next,
        // Default to unlimited when enabling for the first time
        ...(next && { defaultOverageLimitCents: null }),
      })
      toast.success(next ? t('billing.overageEnabled') : t('billing.overageDisabled'))
      onChange?.()
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t('billing.overageUpdateFailed'))
    } finally {
      setPendingToggle(false)
    }
  }

  async function handleSaveCap() {
    let nextCents: number | null

    if (draftMode === 'unlimited') {
      nextCents = null
    } else {
      const parsed = Number(draftText.replace(/[,\s]/g, ''))
      if (!Number.isFinite(parsed) || parsed < MIN_CAP_DOLLARS) {
        toast.error(t('billing.overageCapMinError', { amount: MIN_CAP_DOLLARS }))
        return
      }
      nextCents = Math.round(parsed) * 100
    }

    // No-op if nothing changed
    if (nextCents === defaultOverageLimitCents) {
      setPopoverOpen(false)
      return
    }

    setSavingCap(true)
    try {
      await organizationsApi.updateOverageSettings(orgId, {
        defaultOverageLimitCents: nextCents,
      })
      toast.success(nextCents === null ? t('billing.overageCapRemoved') : t('billing.overageCapUpdated'))
      setPopoverOpen(false)
      onChange?.()
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t('billing.overageCapFailed'))
    } finally {
      setSavingCap(false)
    }
  }

  return (
    <div
      data-testid="billing-overage-controls"
      className="flex flex-col gap-2"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{t('billing.overageTitle')}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('billing.overageKeepRunning')}{' '}
            {allowOverage ? (
              <>
                {isUnlimited ? (
                  <span data-testid="billing-overage-cap-value">{t('billing.overageNoCap')}</span>
                ) : (
                  <span className="tabular-nums text-foreground" data-testid="billing-overage-cap-value">
                    {t('billing.overageCappedAt', { amount: `$${capFormatter.format(committedDollars)}` })}
                  </span>
                )}{' '}
                {readOnly ? null : (
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="billing-overage-cap-change"
                      className="font-medium text-foreground underline-offset-2 hover:underline"
                    >
                      {t('billing.overageChange')}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-64"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        void handleSaveCap()
                      }}
                      className="flex flex-col gap-3"
                    >
                      <p className="text-xs font-medium text-foreground">{t('billing.overageMonthlyCap')}</p>

                      {/* Unlimited option */}
                      <label className="flex cursor-pointer items-center gap-2.5">
                        <input
                          type="radio"
                          name="cap-mode"
                          value="unlimited"
                          checked={draftMode === 'unlimited'}
                          onChange={() => setDraftMode('unlimited')}
                          disabled={savingCap}
                          className="accent-primary"
                        />
                        <span className="text-sm text-foreground">{t('billing.overageUnlimited')}</span>
                      </label>

                      {/* Custom cap option */}
                      <label className="flex cursor-pointer items-start gap-2.5">
                        <input
                          type="radio"
                          name="cap-mode"
                          value="capped"
                          checked={draftMode === 'capped'}
                          onChange={() => setDraftMode('capped')}
                          disabled={savingCap}
                          className="mt-2 accent-primary"
                        />
                        <div className="flex flex-col gap-1 flex-1">
                          <span className="text-sm text-foreground">{t('billing.overageCustomCap')}</span>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                              $
                            </span>
                            <Input
                              id="billing-overage-cap-input"
                              type="text"
                              inputMode="numeric"
                              value={draftText}
                              onChange={(e) =>
                                setDraftText(e.target.value.replace(/[^\d,]/g, ''))
                              }
                              onFocus={(e) => {
                                setDraftMode('capped')
                                e.currentTarget.select()
                              }}
                              disabled={savingCap || draftMode === 'unlimited'}
                              className="pl-6 tabular-nums"
                              data-testid="billing-overage-cap-input"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t('billing.overageMin', { amount: MIN_CAP_DOLLARS })}
                          </p>
                          {overageRateCents > 0 ? (
                            <p className="text-xs text-muted-foreground" data-testid="billing-overage-cap-rate">
                              {t('billing.myPlan.billedAtRate', { rate: formatUsdFromCents(overageRateCents) })}
                            </p>
                          ) : null}
                        </div>
                      </label>

                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setPopoverOpen(false)}
                          disabled={savingCap}
                        >
                          {t('questionnaire.cancel')}
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={savingCap}
                          data-testid="billing-overage-cap-save"
                        >
                          {t('questionnaire.save')}
                        </Button>
                      </div>
                    </form>
                  </PopoverContent>
                </Popover>
                )}
              </>
            ) : (
              <>{t('billing.overageBilledCycle')}</>
            )}
          </p>
          {overageRateCents > 0 ? (
            <p className="mt-0.5 text-sm text-muted-foreground" data-testid="billing-overage-rate">
              {t('billing.myPlan.billedAtRate', { rate: formatUsdFromCents(overageRateCents) })}
            </p>
          ) : null}
        </div>
        {readOnly ? (
          <span
            data-testid="billing-overage-state"
            className="shrink-0 text-sm font-medium text-foreground"
          >
            {allowOverage ? t('billing.myPlan.overageOn') : t('billing.myPlan.overageOff')}
          </span>
        ) : (
          <Switch
            checked={allowOverage}
            onCheckedChange={handleToggle}
            disabled={pendingToggle}
            data-testid="billing-overage-toggle"
            aria-label={t('billing.allowOverageAria')}
          />
        )}
      </div>
    </div>
  )
}
